'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import Badge from '@/components/shared/Badge';
import RemovedContent from '@/components/shared/RemovedContent';
import ReportModal from '@/components/shared/ReportModal';
import ShareModal from '@/components/shared/ShareModal';
import { mockThreads, mockReplies, ConversationThread } from '@/data/mockConversations';
import { ArrowLeft, Share2, ThumbsUp, CheckCircle, Send } from 'lucide-react';
import OwnersOnlyLocked from '@/components/shared/OwnersOnlyLocked';

function AvatarInitials({ name, size = 'md' }: { name: string; size?: 'sm' | 'md' | 'lg' }) {
  const initials = name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
  const dim = size === 'lg' ? 'w-12 h-12 text-[14px]' : size === 'md' ? 'w-9 h-9 text-[12px]' : 'w-7 h-7 text-[10px]';
  return (
    <div
      className={`${dim} rounded-full flex items-center justify-center flex-shrink-0 font-display font-bold`}
      style={{
        background: 'linear-gradient(135deg, rgba(76,201,255,0.20), rgba(122,92,255,0.20))',
        border: '1px solid rgba(76,201,255,0.20)',
        color: '#4CC9FF',
      }}
    >
      {initials}
    </div>
  );
}

function getPostTypeBadge(type: ConversationThread['postType']) {
  const map: Record<ConversationThread['postType'], 'question' | 'recommendation' | 'discussion' | 'offer' | 'alert' | 'poll'> = {
    'Question': 'question',
    'Recommendation Request': 'recommendation',
    'Discussion': 'discussion',
    'Offer': 'offer',
    'Alert': 'alert',
    'Poll': 'poll',
  };
  return map[type];
}

interface ThreadPageProps {
  params: { threadSlug: string };
}

const REMOVED_SLUGS = ['removed-thread'];
const LOCKED_SLUGS = ['locked-thread'];
const OWNERS_ONLY_SLUGS = ['owners-parking-allocation'];

export default function ThreadPage({ params }: ThreadPageProps) {
  const [replyText, setReplyText] = useState('');
  const [reportOpen, setReportOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  if (REMOVED_SLUGS.includes(params.threadSlug)) {
    return (
      <div className="min-h-screen" style={{ background: '#07060C' }}>
        <SiteHeader theme="dark" />
        <main className="pt-16">
          <div style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="max-w-screen-lg mx-auto px-6 lg:px-10 py-4">
              <Link href="/conversations" className="inline-flex items-center gap-2 text-[13px] transition-colors duration-150 hover:opacity-80" style={{ color: '#A7A3C4' }}>
                <ArrowLeft size={14} /> Back to Conversations
              </Link>
            </div>
          </div>
          <div className="max-w-2xl mx-auto px-6 py-20">
            <RemovedContent variant="thread" backHref="/conversations" backLabel="Back to Conversations" />
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  if (OWNERS_ONLY_SLUGS.includes(params.threadSlug)) {
    return (
      <div className="min-h-screen" style={{ background: '#07060C' }}>
        <SiteHeader theme="dark" />
        <main className="pt-16">
          <div style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="max-w-screen-lg mx-auto px-6 lg:px-10 py-4">
              <Link href="/conversations" className="inline-flex items-center gap-2 text-[13px]" style={{ color: '#A7A3C4' }}>
                <ArrowLeft size={14} /> Back to Conversations
              </Link>
            </div>
          </div>
          <div className="max-w-2xl mx-auto px-6 py-16">
            <OwnersOnlyLocked
              title="Owners Only Thread"
              description="This thread is visible to verified unit owners only. Ownership verification is required to access this content."
              backHref="/conversations"
              backLabel="Back to Conversations"
            />
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const thread = mockThreads.find((t) => t.slug === params.threadSlug);

  if (!thread) {
    return (
      <div className="min-h-screen" style={{ background: '#07060C' }}>
        <SiteHeader theme="dark" />
        <main className="pt-16">
          <div style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="max-w-screen-lg mx-auto px-6 lg:px-10 py-4">
              <Link href="/conversations" className="inline-flex items-center gap-2 text-[13px] transition-colors duration-150 hover:opacity-80" style={{ color: '#A7A3C4' }}>
                <ArrowLeft size={14} /> Back to Conversations
              </Link>
            </div>
          </div>
          <div className="max-w-2xl mx-auto px-6 py-20">
            <RemovedContent variant="thread" title="Thread Not Found" description="This thread doesn't exist or may have been removed." backHref="/conversations" backLabel="Back to Conversations" />
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const isLocked = LOCKED_SLUGS.includes(params.threadSlug) || thread.isLocked;
  const replies = mockReplies.filter((r) => r.threadSlug === thread.slug);

  return (
    <div className="min-h-screen" style={{ background: '#07060C' }}>
      <SiteHeader theme="dark" />
      <main className="pt-16">
        {/* Back nav */}
        <div style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-screen-lg mx-auto px-6 lg:px-10 py-4">
            <Link href="/conversations" className="inline-flex items-center gap-2 text-[13px] transition-colors duration-150 hover:opacity-80" style={{ color: '#A7A3C4' }}>
              <ArrowLeft size={14} /> Back to Conversations
            </Link>
          </div>
        </div>

        <div className="max-w-screen-lg mx-auto px-6 lg:px-10 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main thread */}
            <div className="lg:col-span-2 space-y-6">
              {/* Thread header */}
              <div className="neon-card p-6">
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <Badge variant={getPostTypeBadge(thread.postType)} label={thread.postType} size="md" />
                  <span
                    className="text-[12px] px-2.5 py-1 rounded"
                    style={{ background: 'rgba(255,255,255,0.04)', color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}
                  >
                    {thread.category}
                  </span>
                  {thread.isSolved && <Badge variant="solved" size="md" />}
                  {thread.isPinned && <Badge variant="pinned" size="md" />}
                  {isLocked && (
                    <span
                      className="text-[12px] px-2.5 py-1 rounded font-medium"
                      style={{ background: 'rgba(185,123,26,0.10)', color: '#F0A832', border: '1px solid rgba(185,123,26,0.20)' }}
                    >
                      Locked
                    </span>
                  )}
                </div>

                <h1 className="font-display font-bold text-[22px] sm:text-[26px] leading-tight mb-5" style={{ color: '#F2F4FF' }}>
                  {thread.title}
                </h1>

                <div className="flex items-center gap-3 mb-5">
                  <AvatarInitials name={thread.authorName} size="lg" />
                  <div>
                    <p className="font-semibold text-[14px]" style={{ color: '#F2F4FF' }}>{thread.authorName}</p>
                    <p className="text-[12.5px]" style={{ color: '#A7A3C4' }}>
                      {thread.authorRole} · {thread.authorTower} · {thread.date}
                    </p>
                  </div>
                </div>

                <p className="text-[14.5px] leading-relaxed mb-5" style={{ color: 'rgba(242,244,255,0.80)' }}>
                  {thread.body}
                </p>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-5">
                  {thread.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-[12px] px-2.5 py-1 rounded"
                      style={{ background: 'rgba(255,255,255,0.04)', color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Solved notice */}
                {thread.isSolved && (
                  <div
                    className="flex items-center gap-2.5 px-4 py-3 rounded-lg mb-5"
                    style={{ background: 'rgba(45,212,160,0.06)', border: '1px solid rgba(45,212,160,0.15)' }}
                  >
                    <CheckCircle size={15} style={{ color: '#2DD4A0' }} />
                    <p className="text-[13px]" style={{ color: '#2DD4A0' }}>
                      This thread has been marked as solved.
                    </p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-3 pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  <button
                    className="flex items-center gap-1.5 text-[12.5px] px-3 py-1.5 rounded-lg transition-all duration-150 hover:bg-white/[0.06] hover:text-[#F2F4FF] active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(76,201,255,0.35)]"
                    style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}
                  >
                    <ThumbsUp size={13} /> {thread.helpfulCount} Helpful
                  </button>
                  <button
                    onClick={() => setShareOpen(true)}
                    className="flex items-center gap-1.5 text-[12.5px] px-3 py-1.5 rounded-lg transition-all duration-150 hover:bg-white/[0.06] hover:text-[#F2F4FF] active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(76,201,255,0.35)]"
                    style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}
                  >
                    <Share2 size={13} /> Share
                  </button>
                  <button
                    onClick={() => setReportOpen(true)}
                    className="flex items-center gap-1.5 text-[12.5px] px-3 py-1.5 rounded-lg transition-all duration-150 hover:bg-white/[0.06] hover:text-[rgba(255,107,107,0.90)] active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(192,57,43,0.35)]"
                    style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}
                  >
                    Report
                  </button>
                </div>
              </div>

              {/* Replies */}
              <div>
                <h2 className="font-display font-semibold text-[16px] mb-4" style={{ color: '#F2F4FF' }}>
                  {replies.length} {replies.length === 1 ? 'Reply' : 'Replies'}
                </h2>

                {replies.length === 0 ? (
                  <div className="neon-card p-8 text-center">
                    <p className="text-[14px]" style={{ color: '#A7A3C4' }}>No replies yet. Be the first to respond.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {replies.map((reply) => (
                      <div key={reply.id} className="neon-card p-5">
                        {reply.isHelpful && (
                          <div
                            className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg"
                            style={{ background: 'rgba(45,212,160,0.06)', border: '1px solid rgba(45,212,160,0.15)' }}
                          >
                            <CheckCircle size={13} style={{ color: '#2DD4A0' }} />
                            <span className="text-[12px] font-medium" style={{ color: '#2DD4A0' }}>Marked as helpful</span>
                          </div>
                        )}
                        <div className="flex items-start gap-3">
                          <AvatarInitials name={reply.authorName} size="md" />
                          <div className="flex-1">
                            <div className="flex items-center justify-between gap-2 mb-2">
                              <div>
                                <span className="font-semibold text-[13.5px]" style={{ color: '#F2F4FF' }}>{reply.authorName}</span>
                                <span className="text-[12px] ml-2" style={{ color: '#A7A3C4' }}>
                                  {reply.authorRole} · {reply.authorTower}
                                </span>
                              </div>
                              <span className="text-[11.5px]" style={{ color: 'rgba(167,163,196,0.45)' }}>{reply.date}</span>
                            </div>
                            <p className="text-[13.5px] leading-relaxed mb-3" style={{ color: 'rgba(242,244,255,0.75)' }}>
                              {reply.body}
                            </p>
                            <div className="flex items-center gap-3">
                              <button
                                className="flex items-center gap-1.5 text-[12px] transition-all duration-150 hover:text-[#F2F4FF] active:scale-95 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[rgba(76,201,255,0.35)] rounded px-1.5 py-0.5"
                                style={{ color: 'rgba(167,163,196,0.55)' }}
                              >
                                <ThumbsUp size={12} /> {reply.helpfulCount} Helpful
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Reply CTA */}
              {isLocked ? (
                <div
                  className="flex items-center gap-3 px-5 py-4 rounded-xl"
                  style={{ background: 'rgba(185,123,26,0.06)', border: '1px solid rgba(185,123,26,0.18)' }}
                >
                  <span className="text-[13.5px]" style={{ color: '#F0A832' }}>This thread has been locked and no longer accepts new replies.</span>
                </div>
              ) : (
                <div className="neon-card p-5">
                  <h3 className="font-display font-semibold text-[14px] mb-3" style={{ color: '#F2F4FF' }}>Add a Reply</h3>
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Share your thoughts or answer..."
                    rows={4}
                    className="input-neon w-full resize-none mb-3"
                  />
                  <div className="flex items-center justify-between">
                    <p className="text-[12px]" style={{ color: 'rgba(167,163,196,0.45)' }}>
                      Replies are visible to all verified residents.
                    </p>
                    <button className="btn-primary text-[13px] px-4 py-2 flex items-center gap-2">
                      <Send size={13} /> Post Reply
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="hidden lg:block space-y-4">
              <div className="neon-card p-5">
                <h3 className="font-display font-semibold text-[14px] mb-3" style={{ color: '#F2F4FF' }}>Thread Info</h3>
                <div className="space-y-2.5 text-[13px]">
                  <div className="flex justify-between">
                    <span style={{ color: '#A7A3C4' }}>Category</span>
                    <span style={{ color: '#F2F4FF' }}>{thread.category}</span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: '#A7A3C4' }}>Replies</span>
                    <span style={{ color: '#F2F4FF' }}>{thread.replyCount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: '#A7A3C4' }}>Helpful marks</span>
                    <span style={{ color: '#F2F4FF' }}>{thread.helpfulCount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: '#A7A3C4' }}>Last activity</span>
                    <span style={{ color: '#F2F4FF' }}>{thread.lastActivity}</span>
                  </div>
                </div>
              </div>

              <div className="neon-card p-5">
                <h3 className="font-display font-semibold text-[14px] mb-3" style={{ color: '#F2F4FF' }}>Author</h3>
                <div className="flex items-center gap-3">
                  <AvatarInitials name={thread.authorName} size="md" />
                  <div>
                    <Link href={`/directory/${thread.authorName.toLowerCase().replace(' ', '-')}`} className="text-[13px] font-medium transition-colors duration-150 hover:text-[#4CC9FF] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[rgba(76,201,255,0.35)] rounded" style={{ color: '#F2F4FF' }}>
                      {thread.authorName}
                    </Link>
                    <p className="text-[12px]" style={{ color: '#A7A3C4' }}>{thread.authorRole} · {thread.authorTower}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <ReportModal
          isOpen={reportOpen}
          onClose={() => setReportOpen(false)}
          contentType="conversation"
          contentLabel={thread.title}
        />
        <ShareModal
          isOpen={shareOpen}
          onClose={() => setShareOpen(false)}
          contentType="conversation"
          title={thread.title}
        />
      </main>
      <SiteFooter />
    </div>
  );
}
